import { House, FilePdf, Bell, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Invoice, Apartment, User } from './types';
import { formatCurrency, getStatusBadge } from './utils';

interface PaymentHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apartmentId: string | null;
  apartments: Apartment[];
  invoices: Invoice[];
  getResidentForApartment: (apartmentId: string) => User | undefined;
  onDownloadPdf: (invoice: Invoice) => void;
  onSendReminder: (invoice: Invoice) => void;
  onMarkPaid: (invoice: Invoice) => void;
}

export function PaymentHistoryDialog({
  open,
  onOpenChange,
  apartmentId,
  apartments,
  invoices,
  getResidentForApartment,
  onDownloadPdf,
  onSendReminder,
  onMarkPaid
}: PaymentHistoryDialogProps) {
  if (!apartmentId) return null;
  
  const apt = apartments.find(a => a.id === apartmentId);
  const resident = apt ? getResidentForApartment(apt.id) : undefined;
  const aptInvoices = invoices
    .filter(inv => inv.apartment_id === apartmentId)
    .sort((a, b) => b.period.localeCompare(a.period));
  const totalPaid = aptInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total_amount, 0);
  const totalUnpaid = aptInvoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.total_amount, 0);
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <House size={24} className="text-primary" />
            Betalningshistorik - {apt?.apartment_number}
          </DialogTitle>
        </DialogHeader>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 my-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">Boende</p>
            <p className="font-semibold">{resident?.name || 'Ingen'}</p>
            {resident?.email && (
              <p className="text-xs text-slate-400">{resident.email}</p>
            )}
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600">Totalt betalt</p>
            <p className="font-bold text-green-700 text-xl">{formatCurrency(totalPaid)}</p>
          </div>
          <div className={`p-4 rounded-lg ${totalUnpaid > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
            <p className={`text-sm ${totalUnpaid > 0 ? 'text-red-600' : 'text-slate-500'}`}>Obetalt</p>
            <p className={`font-bold text-xl ${totalUnpaid > 0 ? 'text-red-700' : 'text-slate-400'}`}>
              {formatCurrency(totalUnpaid)}
            </p>
          </div>
        </div>

        {/* Payment Timeline */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 border-b">
            <h3 className="font-medium">Fakturor ({aptInvoices.length})</h3>
          </div>
          
          {aptInvoices.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Inga fakturor för denna lägenhet
            </div>
          ) : (
            <div className="divide-y">
              {aptInvoices.map((invoice, index) => {
                const [year, month] = invoice.period.split('-');
                const monthName = monthNames[parseInt(month) - 1];
                
                return (
                  <div key={invoice.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-start gap-4">
                      {/* Timeline indicator */}
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          invoice.status === 'paid' ? 'bg-green-500' :
                          invoice.status === 'overdue' ? 'bg-red-500' :
                          invoice.status === 'sent' ? 'bg-blue-500' :
                          'bg-slate-300'
                        }`} />
                        {index < aptInvoices.length - 1 && (
                          <div className="w-0.5 h-full bg-slate-200 mt-1" style={{ minHeight: '40px' }} />
                        )}
                      </div>
                      
                      {/* Invoice details */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{monthName} {year}</span>
                            <span className="text-slate-400 text-sm ml-2">#{invoice.invoice_number}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold">{formatCurrency(invoice.total_amount)}</span>
                            {getStatusBadge(invoice.status)}
                          </div>
                        </div>
                        
                        {/* Details row */}
                        <div className="mt-2 text-sm text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                          <span>Förfaller: {new Date(invoice.due_date).toLocaleDateString('sv-SE')}</span>
                          {invoice.sent_at && (
                            <span>Skickad: {new Date(invoice.sent_at).toLocaleDateString('sv-SE')}</span>
                          )}
                          {invoice.paid_at && (
                            <span className="text-green-600">
                              Betald: {new Date(invoice.paid_at).toLocaleDateString('sv-SE')}
                            </span>
                          )}
                        </div>
                        
                        {/* Breakdown */}
                        {(invoice.adjustments.length > 0 || invoice.shared_costs.length > 0) && (
                          <div className="mt-2 text-xs text-slate-400">
                            <span>Grund: {formatCurrency(invoice.base_amount)}</span>
                            {invoice.adjustments.map((adj) => (
                              <span key={adj.name} className={adj.amount < 0 ? 'text-green-500' : 'text-orange-500'}>
                                {' '}{adj.amount < 0 ? '' : '+'}{formatCurrency(adj.amount)} ({adj.name})
                              </span>
                            ))}
                            {invoice.shared_costs.map((sc) => (
                              <span key={sc.name}> +{formatCurrency(sc.amount)} ({sc.name})</span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownloadPdf(invoice);
                          }}
                          title="Ladda ner PDF"
                          data-testid={`pdf-btn-${invoice.id}`}
                        >
                          <FilePdf size={16} />
                        </Button>
                        {invoice.status === 'overdue' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSendReminder(invoice);
                            }}
                            title="Skicka påminnelse"
                            data-testid={`reminder-btn-${invoice.id}`}
                          >
                            <Bell size={16} />
                          </Button>
                        )}
                        {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkPaid(invoice);
                            }}
                            title="Markera betald"
                            data-testid={`mark-paid-btn-${invoice.id}`}
                          >
                            <CheckCircle size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Stäng
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
